import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Trash, Camera, MapPin, Package } from "@phosphor-icons/react";

/**
 * PurgeFlagModal — reusable "flag as bulk trash" modal.
 * Photo is required (truck driver needs to see what they're picking up).
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <PurgeFlagModal
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     defaultAddress={job.property_address}
 *     jobId={job.id}
 *     inspectionId={null}
 *     onFlagged={(res) => toast.success("Flagged for truck day")}
 *   />
 */
export default function PurgeFlagModal({
  open,
  onClose,
  defaultAddress = "",
  jobId = null,
  inspectionId = null,
  onFlagged,
}) {
  const fileRef = useRef(null);
  const [address, setAddress] = useState(defaultAddress);
  const [note, setNote] = useState("");
  const [itemKind, setItemKind] = useState("property"); // "property" (whole unit) | "item" (single pile)
  const [roomName, setRoomName] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!file) {
      toast.error("Photo required — the truck driver needs to see what they're picking up");
      return;
    }
    if (!address || address.trim().length < 3) {
      toast.error("Property address required");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("property_address", address.trim());
      fd.append("note", note.trim());
      fd.append("item_kind", itemKind);
      if (roomName.trim()) fd.append("room_name", roomName.trim());
      if (jobId) fd.append("job_id", jobId);
      if (inspectionId) fd.append("inspection_id", inspectionId);
      const r = await api.post("/purge/flag", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Flagged for bulk-trash truck");
      onFlagged?.(r.data);
      onClose?.();
      setFile(null); setPreview(null); setNote(""); setRoomName("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not flag purge"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4"
         data-testid="purge-flag-modal">
      <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[95vh] flex flex-col">
        <div className="p-4 border-b-2 border-amber-500 flex items-center gap-2 bg-amber-50">
          <Trash weight="fill" size={22} className="text-amber-700"/>
          <div className="font-display font-black text-lg uppercase tracking-tight flex-1 text-amber-900">
            Flag for Truck-Day Pickup
          </div>
          <button onClick={onClose} data-testid="purge-modal-close"
                  className="text-slate-500 hover:text-slate-800 h-9 w-9 flex items-center justify-center">
            <X size={20}/>
          </button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto space-y-4">
          {/* Photo — required, top of the flow */}
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-slate-600 mb-1.5 flex items-center gap-1">
              <Camera weight="fill" size={14}/> Photo · required
            </div>
            {preview ? (
              <div className="relative">
                <img src={preview} alt="Purge preview" className="w-full max-h-56 object-cover rounded-lg border-2 border-slate-300"/>
                <button onClick={() => { setFile(null); setPreview(null); }}
                        data-testid="purge-photo-remove"
                        className="absolute top-2 right-2 bg-white/95 rounded-full h-8 w-8 flex items-center justify-center border-2 border-slate-800">
                  <X size={16} weight="bold"/>
                </button>
              </div>
            ) : (
              <button
                data-testid="purge-photo-pick"
                onClick={() => fileRef.current?.click()}
                className="w-full h-32 border-2 border-dashed border-amber-500 rounded-lg bg-amber-50/50 hover:bg-amber-100/50 flex flex-col items-center justify-center gap-1 text-amber-800">
                <Camera weight="fill" size={28}/>
                <div className="text-xs font-black uppercase tracking-widest">Take Photo or Pick</div>
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              data-testid="purge-photo-input"
              onChange={(e) => pickFile(e.target.files?.[0])}
            />
          </div>

          {/* Kind toggle */}
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-slate-600 mb-1.5">
              What kind of purge?
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="purge-kind-property"
                onClick={() => setItemKind("property")}
                className={`h-14 border-2 rounded-lg font-black uppercase text-xs tracking-widest ${
                  itemKind === "property"
                    ? "bg-amber-500 border-amber-700 text-slate-900"
                    : "bg-white border-slate-300 text-slate-700"
                }`}>
                <Package weight="fill" size={18} className="inline mr-1"/>
                Whole Unit
              </button>
              <button
                data-testid="purge-kind-item"
                onClick={() => setItemKind("item")}
                className={`h-14 border-2 rounded-lg font-black uppercase text-xs tracking-widest ${
                  itemKind === "item"
                    ? "bg-amber-500 border-amber-700 text-slate-900"
                    : "bg-white border-slate-300 text-slate-700"
                }`}>
                <Trash weight="fill" size={18} className="inline mr-1"/>
                Single Item
              </button>
            </div>
          </div>

          {/* Address */}
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-slate-600 mb-1.5 flex items-center gap-1">
              <MapPin weight="fill" size={14}/> Property address
            </div>
            <input
              type="text"
              data-testid="purge-address-input"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="123 Main St, Philadelphia"
              className="w-full h-11 px-3 border-2 border-slate-300 rounded-lg font-medium text-sm focus:border-cyan-500 focus:outline-none"
            />
          </div>

          {/* Room (only for single item) */}
          {itemKind === "item" && (
            <div>
              <div className="text-xs font-black uppercase tracking-widest text-slate-600 mb-1.5">
                Room · optional
              </div>
              <input
                type="text"
                data-testid="purge-room-input"
                value={roomName}
                onChange={(e) => setRoomName(e.target.value)}
                placeholder="Kitchen, Bedroom 2, Basement…"
                className="w-full h-11 px-3 border-2 border-slate-300 rounded-lg font-medium text-sm focus:border-cyan-500 focus:outline-none"
              />
            </div>
          )}

          {/* Note */}
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-slate-600 mb-1.5">
              Notes · optional
            </div>
            <textarea
              data-testid="purge-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="e.g. Old sofa + 3 bags of trash · behind unit"
              className="w-full px-3 py-2 border-2 border-slate-300 rounded-lg font-medium text-sm focus:border-cyan-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Sticky submit */}
        <div className="p-4 border-t-2 border-slate-200 bg-slate-50">
          <button
            data-testid="purge-submit-btn"
            onClick={submit}
            disabled={saving || !file}
            className="w-full h-14 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-slate-900 font-display font-black uppercase tracking-widest text-sm rounded-lg border-2 border-slate-900 shadow-[4px_4px_0px_#0f172a] transition-transform hover:translate-x-[1px] hover:translate-y-[1px]">
            {saving ? "Flagging…" : "🚛 Add to Truck Day"}
          </button>
        </div>
      </div>
    </div>
  );
}
